/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  DataTable,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow
} from 'carbon-components-react';
import _ from 'lodash';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@ibm-watson/shared/store';
import { generateDocsUrl } from '../../../../ia/util/docs';
import { selectDraftIntegration } from '../../../store/channels/selectors';
import { SelectEventsMessages as messages } from '../select-events-tab/messages';

interface EventsTableProps {
  disabled: boolean;
  selectionUpdated?: (array) => void;
}

type TableHeaders = {
  header: string;
  key: string;
}[];

type TableRows = {
  description: string;
  event: object;
  id: string;
}[];

export const EventsTable: FC<EventsTableProps> = ({
  disabled,
  selectionUpdated
}) => {
  const { formatMessage: t } = useIntl();

  const draftIntegration = useAppSelector(selectDraftIntegration);
  const enabledEvents = draftIntegration?.settings?.enabled_events;

  const [selectedData, setSelectedData] = useState([]);

  useEffect(() => {
    const prevSelectedEvents = enabledEvents
      ? _.map(enabledEvents, item => item.name)
      : [];
    setSelectedData(prevSelectedEvents);
  }, [enabledEvents]);

  const headers: TableHeaders = [
    {
      header: t(messages.events),
      key: 'event'
    },
    {
      header: t(messages.description),
      key: 'description'
    }
  ];
  const rows: TableRows = [
    {
      description: t(messages.messageHandledDescription),
      event: {
        eventName: t(messages.messagesHandled),
        link: generateDocsUrl(
          '/watson-assistant?topic=watson-assistant-segment-event-reference#segment-event-reference-message-handled'
        )
      },
      id: 'message_handled'
    },
    {
      description: t(messages.sessionStartedDescription),
      event: {
        eventName: t(messages.sessionStarted),
        link: generateDocsUrl(
          '/watson-assistant?topic=watson-assistant-segment-event-reference#segment-event-reference-session-started'
        )
      },
      id: 'session_started'
    },
    {
      description: t(messages.actionStartedDescription),
      event: {
        eventName: t(messages.actionStarted),
        link: generateDocsUrl(
          '/watson-assistant?topic=watson-assistant-segment-event-reference#segment-event-reference-action-started'
        )
      },
      id: 'action_started'
    },
    {
      description: t(messages.actionCompletedDescription),
      event: {
        eventName: t(messages.actionCompleted),
        link: generateDocsUrl(
          '/watson-assistant?topic=watson-assistant-segment-event-reference#segment-event-reference-action-completed'
        )
      },
      id: 'action_completed'
    }
  ];

  const handleRowSelect = row => {
    const isThere = selectedData.some(i => i === row.id);
    const revisedSelectedData = isThere
      ? selectedData.filter(i => i !== row.id)
      : selectedData.concat(row.id);
    setSelectedData(revisedSelectedData);
    selectionUpdated(revisedSelectedData);
  };

  const handleSelectAll = () => {
    const selectedDataLength = selectedData.length;
    if (selectedDataLength >= 0 && selectedDataLength < 4) {
      // not all already selected, so select all
      const ids = [];
      rows.forEach(row => {
        ids.push(row.id);
      });
      setSelectedData(ids);
      selectionUpdated(ids);
    } else {
      // already all selected, so deselect all
      setSelectedData([]);
      selectionUpdated([]);
    }
  };

  return (
    <DataTable headers={headers} rows={rows}>
      {({
        rows,
        headers,
        getTableProps,
        getHeaderProps,
        getRowProps,
        getSelectionProps
      }) => (
        <Table {...getTableProps()}>
          <TableHead>
            <TableRow>
              <TableSelectAll
                {...getSelectionProps()}
                onSelect={() => handleSelectAll()}
                checked={
                  selectedData.length > 0 && selectedData.length === rows.length
                }
                indeterminate={
                  selectedData.length > 0 && selectedData.length < 4
                }
                disabled={disabled}
              />
              {headers.map(header => (
                <TableHeader {...getHeaderProps({ header })} key={header.key}>
                  {header.header}
                </TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow {...getRowProps({ row })} key={row.id}>
                <TableSelectRow
                  {...getSelectionProps({ row })}
                  onSelect={() => handleRowSelect(row)}
                  checked={selectedData?.some(i => i === row.id)}
                  disabled={disabled}
                />
                {row.cells.map(cell => (
                  <TableCell key={cell.id}>
                    {cell?.info?.header === 'event' ? (
                      <Link
                        href={cell.value.link}
                        target="_blank"
                        rel="noopener noreferrer">
                        {cell.value.eventName}
                      </Link>
                    ) : (
                      <>{cell.value}</>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </DataTable>
  );
};

export default EventsTable;
